// Debug menu - activated by typing "iddqd" or double-tapping the clock
import * as THREE from 'three';
import { LOT_OPEN_COST, QUALITY_TIER_THRESHOLDS } from '../config/balance.js';

/**
 * Helper to create option elements
 */
function createOption(value, text, style = null) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = text;
    if (style) opt.style.cssText = style;
    return opt;
}

/**
 * Helper to create a section header (h3)
 */
function createSectionHeader(text, bgColor = '#e94560') {
    const h3 = document.createElement('h3');
    h3.style.cssText = `margin: 0 0 15px 0; color: #0f3460; background: ${bgColor}; padding: 5px 10px; border-radius: 5px;`;
    h3.textContent = text;
    return h3;
}

/**
 * Helper to create a placeholder paragraph
 */
function createPlaceholder(text, color = '#666') {
    const p = document.createElement('p');
    p.style.cssText = `color: ${color}; margin: 0; text-align: center;`;
    p.textContent = text;
    return p;
}

/**
 * Helper to clear all children from an element
 */
function clearChildren(element) {
    while (element.firstChild) {
        element.removeChild(element.firstChild);
    }
}

/**
 * Helper to create a diagnostic grid row with label and value
 */
function createDiagRow(label, value, valueColor = '#fff') {
    const labelSpan = document.createElement('span');
    labelSpan.style.color = '#aaa';
    labelSpan.textContent = label;

    const valueSpan = document.createElement('span');
    valueSpan.style.color = valueColor;
    valueSpan.textContent = value;

    return [labelSpan, valueSpan];
}

/**
 * Helper to create a warning paragraph
 */
function createWarning(text) {
    const p = document.createElement('p');
    p.style.cssText = 'color: #e74c3c; margin: 10px 0 0 0; font-size: 11px;';
    p.textContent = text;
    return p;
}

/**
 * Helper to create an info paragraph
 */
function createInfo(text, color = '#9b59b6') {
    const p = document.createElement('p');
    p.style.cssText = `color: ${color}; margin: 10px 0 0 0; font-size: 11px;`;
    p.textContent = text;
    return p;
}

/**
 * Helper to create a perf stats row (flex between)
 */
function createPerfRow(label, value, valueColor) {
    const row = document.createElement('div');
    row.style.cssText = 'display: flex; justify-content: space-between; margin-bottom: 4px;';

    const labelSpan = document.createElement('span');
    labelSpan.style.color = '#888';
    labelSpan.textContent = label;
    row.appendChild(labelSpan);

    const valueSpan = document.createElement('span');
    valueSpan.style.color = valueColor;
    valueSpan.textContent = value;
    row.appendChild(valueSpan);

    row._valueSpan = valueSpan;
    return row;
}

/**
 * Helper to create a divider for perf stats
 */
function createPerfDivider() {
    const div = document.createElement('div');
    div.style.cssText = 'border-top: 1px solid rgba(255,255,255,0.1); margin: 6px 0;';
    return div;
}

export class DebugMenu {
    constructor(gameState, itemGenerator, itemsData) {
        this.gameState = gameState;
        this.itemGenerator = itemGenerator;
        this.itemsData = itemsData;
        this.isOpen = false;
        this.currentItem = null;
        this.keyBuffer = '';
        this.keyBufferTimeout = null;
        this.wavePickupManager = null;

        this.createModal();
        this.setupKeyListener();
    }

    /**
     * Set the wave pickup manager (called from main.js after initialization)
     */
    setWavePickupManager(manager) {
        this.wavePickupManager = manager;
    }

    /**
     * Set the GameUI reference (for modal viewer)
     */
    setGameUI(gameUI) {
        this.gameUI = gameUI;
    }

    setupKeyListener() {
        document.addEventListener('keydown', (e) => {
            // Don't capture if typing in an input or textarea
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            // Don't capture if a modal overlay is present
            if (document.querySelector('.cf-modal-overlay')) return;

            // Add key to buffer
            this.keyBuffer += e.key.toLowerCase();

            // Keep only last 5 characters
            if (this.keyBuffer.length > 5) {
                this.keyBuffer = this.keyBuffer.slice(-5);
            }

            // Check for "iddqd"
            if (this.keyBuffer === 'iddqd') {
                this.toggle();
                this.keyBuffer = '';
            }

            // Clear buffer after 2 seconds of no typing
            clearTimeout(this.keyBufferTimeout);
            this.keyBufferTimeout = setTimeout(() => {
                this.keyBuffer = '';
            }, 2000);
        });
    }

    createModal() {
        // Create overlay
        this.overlay = document.createElement('div');
        this.overlay.id = 'debug-overlay';
        this.overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.8);
            z-index: 10000;
            display: none;
            justify-content: center;
            align-items: center;
            font-family: 'Courier New', monospace;
        `;
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) this.close();
        });

        // Create modal container
        const modal = document.createElement('div');
        modal.id = 'debug-modal';
        modal.style.cssText = `
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
            border: 2px solid #e94560;
            border-radius: 10px;
            padding: 20px;
            min-width: 350px;
            max-width: 450px;
            max-height: 80vh;
            overflow-y: auto;
            color: #fff;
            box-shadow: 0 0 30px rgba(233, 69, 96, 0.3);
        `;

        // Header
        const header = document.createElement('div');
        header.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 1px solid #e94560;
        `;
        const h2 = document.createElement('h2');
        h2.style.cssText = 'margin: 0; color: #e94560; font-size: 18px;';
        h2.textContent = 'DEBUG MENU';
        header.appendChild(h2);

        const hint = document.createElement('span');
        hint.style.cssText = 'color: #666; font-size: 12px;';
        hint.textContent = 'iddqd / 2x tap clock';
        header.appendChild(hint);

        const closeBtn = document.createElement('button');
        closeBtn.textContent = 'X';
        closeBtn.style.cssText = `
            background: none;
            border: 1px solid #e94560;
            color: #e94560;
            width: 30px;
            height: 30px;
            border-radius: 5px;
            cursor: pointer;
            font-weight: bold;
        `;
        closeBtn.addEventListener('click', () => this.close());
        header.appendChild(closeBtn);
        modal.appendChild(header);

        // Prevent Safari autofill from crashing on HTTP (crypto.randomUUID unavailable)
        // Stop focusin events from propagating to autofill handlers
        modal.addEventListener('focusin', (e) => {
            e.stopPropagation();
        }, true);

        // Item Generator Section
        const itemSection = document.createElement('div');
        itemSection.appendChild(createSectionHeader('Item Generator'));

        // Controls row
        const controlsRow = document.createElement('div');
        controlsRow.style.cssText = `
            display: flex;
            gap: 10px;
            margin-bottom: 15px;
        `;

        // Base item select
        const baseItemSelect = document.createElement('select');
        baseItemSelect.id = 'debug-base-item';
        baseItemSelect.autocomplete = 'off';
        baseItemSelect.setAttribute('data-lpignore', 'true');
        baseItemSelect.setAttribute('data-form-type', 'other');
        baseItemSelect.style.cssText = `
            flex: 1;
            padding: 8px;
            border-radius: 5px;
            border: 1px solid #0f3460;
            background: #1a1a2e;
            color: #fff;
            font-family: inherit;
        `;
        baseItemSelect.appendChild(createOption('', 'Random Item'));
        this.itemGenerator.baseItems.forEach(item => {
            baseItemSelect.appendChild(createOption(item.id, `${item.name} (${item.slot})`));
        });
        controlsRow.appendChild(baseItemSelect);
        this.baseItemSelect = baseItemSelect;

        // Rarity select
        const raritySelect = document.createElement('select');
        raritySelect.id = 'debug-rarity';
        raritySelect.autocomplete = 'off';
        raritySelect.setAttribute('data-lpignore', 'true');
        raritySelect.setAttribute('data-form-type', 'other');
        raritySelect.style.cssText = `
            flex: 1;
            padding: 8px;
            border-radius: 5px;
            border: 1px solid #0f3460;
            background: #1a1a2e;
            color: #fff;
            font-family: inherit;
        `;
        const rarities = ['faded', 'standard', 'pristine', 'golden', 'vectorized'];
        rarities.forEach(r => {
            const color = this.itemsData.rarityColors[r];
            raritySelect.appendChild(createOption(r, r.charAt(0).toUpperCase() + r.slice(1), `color: ${color}`));
        });
        controlsRow.appendChild(raritySelect);
        this.raritySelect = raritySelect;

        itemSection.appendChild(controlsRow);

        // Generate button
        const generateBtn = document.createElement('button');
        generateBtn.textContent = 'Generate Item';
        generateBtn.style.cssText = `
            width: 100%;
            padding: 10px;
            background: #0f3460;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            font-size: 14px;
            margin-bottom: 15px;
            transition: background 0.2s;
        `;
        generateBtn.addEventListener('mouseover', () => generateBtn.style.background = '#1a4a7a');
        generateBtn.addEventListener('mouseout', () => generateBtn.style.background = '#0f3460');
        generateBtn.addEventListener('click', () => this.generateItem());
        itemSection.appendChild(generateBtn);

        // Item preview area
        const previewArea = document.createElement('div');
        previewArea.id = 'debug-item-preview';
        previewArea.style.cssText = `
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid #0f3460;
            border-radius: 5px;
            padding: 15px;
            min-height: 100px;
            margin-bottom: 15px;
        `;
        previewArea.appendChild(createPlaceholder('Click "Generate Item" to create an item'));
        itemSection.appendChild(previewArea);
        this.previewArea = previewArea;

        // Add to inventory button
        const addBtn = document.createElement('button');
        addBtn.textContent = 'Add to Inventory';
        addBtn.style.cssText = `
            width: 100%;
            padding: 10px;
            background: #27ae60;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            font-size: 14px;
            display: none;
            transition: background 0.2s;
        `;
        addBtn.addEventListener('mouseover', () => addBtn.style.background = '#2ecc71');
        addBtn.addEventListener('mouseout', () => addBtn.style.background = '#27ae60');
        addBtn.addEventListener('click', () => this.addItemToInventory());
        itemSection.appendChild(addBtn);
        this.addBtn = addBtn;

        modal.appendChild(itemSection);

        // Add miles section
        const milesSection = document.createElement('div');
        milesSection.style.cssText = 'margin-top: 20px; padding-top: 15px; border-top: 1px solid #0f3460;';
        milesSection.appendChild(createSectionHeader('Resources'));

        const milesRow = document.createElement('div');
        milesRow.style.cssText = 'display: flex; gap: 10px;';

        const milesInput = document.createElement('input');
        milesInput.type = 'number';
        milesInput.value = '100';
        milesInput.autocomplete = 'off';
        milesInput.setAttribute('data-lpignore', 'true');
        milesInput.setAttribute('data-form-type', 'other');
        milesInput.style.cssText = `
            flex: 1;
            padding: 8px;
            border-radius: 5px;
            border: 1px solid #0f3460;
            background: #1a1a2e;
            color: #fff;
            font-family: inherit;
        `;
        milesRow.appendChild(milesInput);

        const addMilesBtn = document.createElement('button');
        addMilesBtn.textContent = 'Add Miles';
        addMilesBtn.style.cssText = `
            padding: 8px 15px;
            background: #f39c12;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
        `;
        addMilesBtn.addEventListener('click', () => {
            const amount = parseInt(milesInput.value) || 0;
            this.gameState.state.miles += amount;
            this.gameState.state.totalMiles += amount;
            this.gameState.save();
            this.showNotification(`Added ${amount} miles!`);
        });
        milesRow.appendChild(addMilesBtn);

        milesSection.appendChild(milesRow);

        // Wavedust row
        const wavedustRow = document.createElement('div');
        wavedustRow.style.cssText = 'display: flex; gap: 10px; margin-top: 10px;';

        const wavedustInput = document.createElement('input');
        wavedustInput.type = 'number';
        wavedustInput.value = '100';
        wavedustInput.autocomplete = 'off';
        wavedustInput.setAttribute('data-lpignore', 'true');
        wavedustInput.setAttribute('data-form-type', 'other');
        wavedustInput.style.cssText = `
            flex: 1;
            padding: 8px;
            border-radius: 5px;
            border: 1px solid #0f3460;
            background: #1a1a2e;
            color: #fff;
            font-family: inherit;
        `;
        wavedustRow.appendChild(wavedustInput);

        const addWavedustBtn = document.createElement('button');
        addWavedustBtn.textContent = 'Add Salvage';
        addWavedustBtn.style.cssText = `
            padding: 8px 15px;
            background: #a78bfa;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
        `;
        addWavedustBtn.addEventListener('click', () => {
            const amount = parseInt(wavedustInput.value) || 0;
            this.gameState.addWavedust(amount);
            this.showNotification(`Added ${amount} Salvage!`);
        });
        wavedustRow.appendChild(addWavedustBtn);

        milesSection.appendChild(wavedustRow);
        modal.appendChild(milesSection);

        // Wave Debug section
        const waveSection = document.createElement('div');
        waveSection.style.cssText = 'margin-top: 20px; padding-top: 15px; border-top: 1px solid #0f3460;';
        waveSection.appendChild(createSectionHeader('Lot Debug', '#9b59b6'));

        const waveInfo = document.createElement('div');
        waveInfo.id = 'debug-wave-info';
        waveInfo.style.cssText = `
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid #0f3460;
            border-radius: 5px;
            padding: 10px;
            font-size: 12px;
            font-family: monospace;
            margin-bottom: 10px;
        `;
        waveInfo.appendChild(createPlaceholder('Click "Refresh" to see wave state'));
        waveSection.appendChild(waveInfo);
        this.waveInfo = waveInfo;

        const waveRefreshBtn = document.createElement('button');
        waveRefreshBtn.textContent = 'Refresh Wave Info';
        waveRefreshBtn.style.cssText = `
            width: 100%;
            padding: 8px;
            background: #9b59b6;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            margin-bottom: 10px;
        `;
        waveRefreshBtn.addEventListener('click', () => this.refreshWaveInfo());
        waveSection.appendChild(waveRefreshBtn);

        const fixWaveBtn = document.createElement('button');
        fixWaveBtn.textContent = 'Fix Next Milestone';
        fixWaveBtn.style.cssText = `
            width: 100%;
            padding: 8px;
            background: #e74c3c;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
        `;
        fixWaveBtn.addEventListener('click', () => this.fixNextMilestone());
        waveSection.appendChild(fixWaveBtn);

        // Generate Random Wave button
        const generateWaveBtn = document.createElement('button');
        generateWaveBtn.textContent = 'Generate Random Wave';
        generateWaveBtn.style.cssText = `
            width: 100%;
            padding: 8px;
            background: #27ae60;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            margin-top: 10px;
        `;
        generateWaveBtn.addEventListener('click', () => this.generateRandomWave());
        waveSection.appendChild(generateWaveBtn);

        // Spawn Test Pickup button (spawns a wave model 100 units ahead for visual testing)
        const spawnTestBtn = document.createElement('button');
        spawnTestBtn.textContent = 'Spawn Test Pickup (100 units ahead)';
        spawnTestBtn.style.cssText = `
            width: 100%;
            padding: 8px;
            background: #8e44ad;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            margin-top: 10px;
        `;
        spawnTestBtn.addEventListener('click', () => this.spawnTestPickup());
        waveSection.appendChild(spawnTestBtn);

        modal.appendChild(waveSection);

        // Speed diagnostics section
        const speedSection = document.createElement('div');
        speedSection.style.cssText = 'margin-top: 20px; padding-top: 15px; border-top: 1px solid #0f3460;';
        speedSection.appendChild(createSectionHeader('Speed Diagnostics'));

        const speedDiagnostics = document.createElement('div');
        speedDiagnostics.id = 'debug-speed-diagnostics';
        speedDiagnostics.style.cssText = `
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid #0f3460;
            border-radius: 5px;
            padding: 10px;
            font-size: 12px;
            font-family: monospace;
        `;
        speedDiagnostics.appendChild(createPlaceholder('Click "Refresh" to see current values'));
        speedSection.appendChild(speedDiagnostics);
        this.speedDiagnostics = speedDiagnostics;

        const refreshBtn = document.createElement('button');
        refreshBtn.textContent = 'Refresh Diagnostics';
        refreshBtn.style.cssText = `
            width: 100%;
            padding: 8px;
            background: #3498db;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
            margin-top: 10px;
        `;
        refreshBtn.addEventListener('click', () => this.refreshSpeedDiagnostics());
        speedSection.appendChild(refreshBtn);

        modal.appendChild(speedSection);

        // Time Control section
        const timeSection = document.createElement('div');
        timeSection.style.cssText = 'margin-top: 20px; padding-top: 15px; border-top: 1px solid #0f3460;';
        timeSection.appendChild(createSectionHeader('Time Control'));

        // Current time display
        const timeDisplay = document.createElement('div');
        timeDisplay.id = 'debug-time-display';
        timeDisplay.style.cssText = `
            text-align: center;
            font-size: 24px;
            color: #f39c12;
            margin-bottom: 15px;
            font-weight: bold;
        `;
        timeDisplay.textContent = this.gameState.getGameTimeString();
        timeSection.appendChild(timeDisplay);
        this.timeDisplay = timeDisplay;

        // Time slider container
        const sliderContainer = document.createElement('div');
        sliderContainer.style.cssText = 'margin-bottom: 10px;';

        // Time labels
        const timeLabels = document.createElement('div');
        timeLabels.style.cssText = `
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #666;
            margin-bottom: 5px;
        `;
        ['12 AM', '6 AM', '12 PM', '6 PM', '12 AM'].forEach(text => {
            const span = document.createElement('span');
            span.textContent = text;
            timeLabels.appendChild(span);
        });
        sliderContainer.appendChild(timeLabels);

        // Time slider
        const timeSlider = document.createElement('input');
        timeSlider.type = 'range';
        timeSlider.min = '0';
        timeSlider.max = '86400';
        timeSlider.step = '60'; // 1-minute increments
        timeSlider.value = this.gameState.state.gameTime;
        timeSlider.style.cssText = `
            width: 100%;
            height: 8px;
            -webkit-appearance: none;
            background: linear-gradient(to right,
                #1a1a2e 0%,
                #ff6b35 25%,
                #87ceeb 50%,
                #ff6b35 75%,
                #1a1a2e 100%
            );
            border-radius: 4px;
            outline: none;
            cursor: pointer;
        `;

        // Style the slider thumb
        const sliderStyle = document.createElement('style');
        sliderStyle.textContent = `
            #debug-time-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                width: 16px;
                height: 16px;
                background: #e94560;
                border-radius: 50%;
                cursor: pointer;
                box-shadow: 0 0 5px rgba(233, 69, 96, 0.5);
            }
            #debug-time-slider::-moz-range-thumb {
                width: 16px;
                height: 16px;
                background: #e94560;
                border-radius: 50%;
                cursor: pointer;
                border: none;
                box-shadow: 0 0 5px rgba(233, 69, 96, 0.5);
            }
        `;
        document.head.appendChild(sliderStyle);

        timeSlider.id = 'debug-time-slider';
        timeSlider.addEventListener('input', (e) => {
            const newTime = parseInt(e.target.value);
            this.gameState.state.gameTime = newTime;
            this.updateTimeDisplay();
        });
        sliderContainer.appendChild(timeSlider);
        this.timeSlider = timeSlider;

        timeSection.appendChild(sliderContainer);

        // Quick time buttons
        const quickTimeRow = document.createElement('div');
        quickTimeRow.style.cssText = 'display: flex; gap: 5px; flex-wrap: wrap;';

        const quickTimes = [
            { label: 'Dawn', time: 21600 },    // 6 AM
            { label: 'Noon', time: 43200 },    // 12 PM
            { label: 'Sunset', time: 64800 },  // 6 PM
            { label: 'Night', time: 0 }        // 12 AM
        ];

        quickTimes.forEach(({ label, time }) => {
            const btn = document.createElement('button');
            btn.textContent = label;
            btn.style.cssText = `
                flex: 1;
                padding: 6px 10px;
                background: #0f3460;
                border: none;
                border-radius: 5px;
                color: #fff;
                cursor: pointer;
                font-family: inherit;
                font-size: 12px;
                transition: background 0.2s;
            `;
            btn.addEventListener('mouseover', () => btn.style.background = '#1a4a7a');
            btn.addEventListener('mouseout', () => btn.style.background = '#0f3460');
            btn.addEventListener('click', () => {
                this.gameState.state.gameTime = time;
                this.timeSlider.value = time;
                this.updateTimeDisplay();
            });
            quickTimeRow.appendChild(btn);
        });

        timeSection.appendChild(quickTimeRow);
        modal.appendChild(timeSection);

        // Display Options section
        const displaySection = document.createElement('div');
        displaySection.style.cssText = 'margin-top: 20px; padding-top: 15px; border-top: 1px solid #0f3460;';
        displaySection.appendChild(createSectionHeader('Display Options'));

        // Rebase debug checkbox
        const rebaseCheckRow = document.createElement('div');
        rebaseCheckRow.style.cssText = 'display: flex; align-items: center; gap: 10px; margin-bottom: 10px;';

        const rebaseCheckbox = document.createElement('input');
        rebaseCheckbox.type = 'checkbox';
        rebaseCheckbox.id = 'debug-rebase-panel';
        rebaseCheckbox.checked = false; // Default to disabled
        rebaseCheckbox.style.cssText = `
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #e94560;
        `;
        rebaseCheckbox.addEventListener('change', (e) => {
            const app = window.roadTripApp;
            if (app && app.debugPanel) {
                app.debugPanel.setEnabled(e.target.checked);
            }
        });
        rebaseCheckRow.appendChild(rebaseCheckbox);

        const rebaseLabel = document.createElement('label');
        rebaseLabel.htmlFor = 'debug-rebase-panel';
        rebaseLabel.textContent = 'Show World Debug Panel';
        rebaseLabel.style.cssText = 'color: #fff; cursor: pointer; font-size: 14px;';
        rebaseCheckRow.appendChild(rebaseLabel);

        displaySection.appendChild(rebaseCheckRow);

        // Performance stats checkbox
        const perfCheckRow = document.createElement('div');
        perfCheckRow.style.cssText = 'display: flex; align-items: center; gap: 10px;';

        const perfCheckbox = document.createElement('input');
        perfCheckbox.type = 'checkbox';
        perfCheckbox.id = 'debug-perf-stats';
        perfCheckbox.checked = false;
        perfCheckbox.style.cssText = `
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #e94560;
        `;
        perfCheckbox.addEventListener('change', (e) => {
            this.togglePerfStats(e.target.checked);
        });
        perfCheckRow.appendChild(perfCheckbox);

        const perfLabel = document.createElement('label');
        perfLabel.htmlFor = 'debug-perf-stats';
        perfLabel.textContent = 'Show Performance Stats';
        perfLabel.style.cssText = 'color: #fff; cursor: pointer; font-size: 14px;';
        perfCheckRow.appendChild(perfLabel);

        displaySection.appendChild(perfCheckRow);

        // === Click Performance Diagnostic Toggles ===
        const clickPerfHeader = document.createElement('div');
        clickPerfHeader.style.cssText = 'margin-top: 15px; margin-bottom: 8px; color: #e94560; font-size: 12px; font-weight: bold;';
        clickPerfHeader.textContent = 'CLICK PERF DIAGNOSTICS';
        displaySection.appendChild(clickPerfHeader);

        // Disable popups checkbox
        const noPopupsRow = document.createElement('div');
        noPopupsRow.style.cssText = 'display: flex; align-items: center; gap: 10px;';

        const noPopupsCheckbox = document.createElement('input');
        noPopupsCheckbox.type = 'checkbox';
        noPopupsCheckbox.id = 'debug-no-popups';
        noPopupsCheckbox.checked = false;
        noPopupsCheckbox.style.cssText = 'width: 18px; height: 18px; cursor: pointer; accent-color: #e94560;';
        noPopupsCheckbox.addEventListener('change', (e) => {
            window._debugDisablePopups = e.target.checked;
        });
        noPopupsRow.appendChild(noPopupsCheckbox);

        const noPopupsLabel = document.createElement('label');
        noPopupsLabel.htmlFor = 'debug-no-popups';
        noPopupsLabel.textContent = 'Disable Click Popups';
        noPopupsLabel.style.cssText = 'color: #fff; cursor: pointer; font-size: 14px;';
        noPopupsRow.appendChild(noPopupsLabel);
        displaySection.appendChild(noPopupsRow);

        // Disable rev sounds checkbox
        const noRevRow = document.createElement('div');
        noRevRow.style.cssText = 'display: flex; align-items: center; gap: 10px; margin-top: 5px;';

        const noRevCheckbox = document.createElement('input');
        noRevCheckbox.type = 'checkbox';
        noRevCheckbox.id = 'debug-no-rev';
        noRevCheckbox.checked = false;
        noRevCheckbox.style.cssText = 'width: 18px; height: 18px; cursor: pointer; accent-color: #e94560;';
        noRevCheckbox.addEventListener('change', (e) => {
            window._debugDisableRevSound = e.target.checked;
        });
        noRevRow.appendChild(noRevCheckbox);

        const noRevLabel = document.createElement('label');
        noRevLabel.htmlFor = 'debug-no-rev';
        noRevLabel.textContent = 'Disable Rev Sounds';
        noRevLabel.style.cssText = 'color: #fff; cursor: pointer; font-size: 14px;';
        noRevRow.appendChild(noRevLabel);
        displaySection.appendChild(noRevRow);

        // Free camera checkbox
        const freeCamCheckRow = document.createElement('div');
        freeCamCheckRow.style.cssText = 'display: flex; align-items: center; gap: 10px; margin-top: 10px;';

        const freeCamCheckbox = document.createElement('input');
        freeCamCheckbox.type = 'checkbox';
        freeCamCheckbox.id = 'debug-free-camera';
        freeCamCheckbox.checked = false;
        freeCamCheckbox.style.cssText = `
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #e94560;
        `;
        freeCamCheckbox.addEventListener('change', (e) => {
            this.toggleFreeCamera(e.target.checked);
        });
        freeCamCheckRow.appendChild(freeCamCheckbox);

        const freeCamLabel = document.createElement('label');
        freeCamLabel.htmlFor = 'debug-free-camera';
        freeCamLabel.textContent = 'Free Camera (WASD + Mouse)';
        freeCamLabel.style.cssText = 'color: #fff; cursor: pointer; font-size: 14px;';
        freeCamCheckRow.appendChild(freeCamLabel);

        displaySection.appendChild(freeCamCheckRow);

        // Look-at sphere checkbox
        const lookAtCheckRow = document.createElement('div');
        lookAtCheckRow.style.cssText = 'display: flex; align-items: center; gap: 10px; margin-top: 10px;';

        const lookAtCheckbox = document.createElement('input');
        lookAtCheckbox.type = 'checkbox';
        lookAtCheckbox.id = 'debug-lookat-sphere';
        lookAtCheckbox.checked = false;
        lookAtCheckbox.style.cssText = `
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #e94560;
        `;
        lookAtCheckbox.addEventListener('change', (e) => {
            const app = window.roadTripApp;
            if (app && app.cameraController) {
                app.cameraController.setDebugLookAtEnabled(e.target.checked);
            }
        });
        lookAtCheckRow.appendChild(lookAtCheckbox);

        const lookAtLabel = document.createElement('label');
        lookAtLabel.htmlFor = 'debug-lookat-sphere';
        lookAtLabel.textContent = 'Show Camera Look-At Sphere';
        lookAtLabel.style.cssText = 'color: #fff; cursor: pointer; font-size: 14px;';
        lookAtCheckRow.appendChild(lookAtLabel);

        displaySection.appendChild(lookAtCheckRow);

        modal.appendChild(displaySection);

        // Modal Viewer section
        const modalSection = document.createElement('div');
        modalSection.style.cssText = 'margin-top: 20px; padding-top: 15px; border-top: 1px solid #0f3460;';
        modalSection.appendChild(createSectionHeader('Modal Viewer', '#3498db'));

        const modalRow = document.createElement('div');
        modalRow.style.cssText = 'display: flex; gap: 10px;';

        const modalSelect = document.createElement('select');
        modalSelect.id = 'debug-modal-select';
        modalSelect.autocomplete = 'off';
        modalSelect.setAttribute('data-lpignore', 'true');
        modalSelect.setAttribute('data-form-type', 'other');
        modalSelect.style.cssText = `
            flex: 1;
            padding: 8px;
            border-radius: 5px;
            border: 1px solid #0f3460;
            background: #1a1a2e;
            color: #fff;
            font-family: inherit;
        `;
        const modals = [
            { id: 'waveTutorial', name: 'Wave Tutorial' },
            { id: 'tierUpgrade', name: 'Tier Upgrade' },
            { id: 'saveConflict', name: 'Save Conflict' }
        ];
        modals.forEach(m => {
            modalSelect.appendChild(createOption(m.id, m.name));
        });
        modalRow.appendChild(modalSelect);

        const showModalBtn = document.createElement('button');
        showModalBtn.textContent = 'Show';
        showModalBtn.style.cssText = `
            padding: 8px 20px;
            background: #3498db;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
        `;
        showModalBtn.addEventListener('click', () => this.showDebugModal(modalSelect.value));
        modalRow.appendChild(showModalBtn);

        modalSection.appendChild(modalRow);
        modal.appendChild(modalSection);

        // Bug Report Export section
        const bugReportSection = document.createElement('div');
        bugReportSection.style.cssText = 'margin-top: 20px; padding-top: 15px; border-top: 1px solid #0f3460;';
        bugReportSection.appendChild(createSectionHeader('Bug Report Export'));

        const bugReportRow = document.createElement('div');
        bugReportRow.style.cssText = 'display: flex; gap: 10px; margin-bottom: 10px;';

        const bugReportInput = document.createElement('input');
        bugReportInput.type = 'text';
        bugReportInput.placeholder = 'Document ID (e.g., 0gBfSkBN7RGgKTigx88h)';
        bugReportInput.autocomplete = 'off';
        bugReportInput.setAttribute('data-lpignore', 'true');
        bugReportInput.setAttribute('data-form-type', 'other');
        bugReportInput.style.cssText = `
            flex: 1;
            padding: 8px;
            border-radius: 5px;
            border: 1px solid #0f3460;
            background: #1a1a2e;
            color: #fff;
            font-family: inherit;
            font-size: 12px;
        `;
        bugReportRow.appendChild(bugReportInput);
        bugReportSection.appendChild(bugReportRow);

        const exportBugReportBtn = document.createElement('button');
        exportBugReportBtn.textContent = 'Export to JSON';
        exportBugReportBtn.style.cssText = `
            width: 100%;
            padding: 8px;
            background: #e74c3c;
            border: none;
            border-radius: 5px;
            color: #fff;
            cursor: pointer;
            font-family: inherit;
        `;
        exportBugReportBtn.addEventListener('click', () => this.exportBugReport(bugReportInput.value.trim()));
        bugReportSection.appendChild(exportBugReportBtn);

        modal.appendChild(bugReportSection);

        this.overlay.appendChild(modal);
        document.body.appendChild(this.overlay);
    }

    updateTimeDisplay() {
        if (this.timeDisplay) {
            this.timeDisplay.textContent = this.gameState.getGameTimeString();
        }
    }

    refreshSpeedDiagnostics() {
        // Access the app's drive state machine through the global
        const app = window.roadTripApp;
        clearChildren(this.speedDiagnostics);

        if (!app || !app.driveStateMachine) {
            this.speedDiagnostics.appendChild(createPlaceholder('Game not loaded yet', '#e74c3c'));
            return;
        }

        const dsm = app.driveStateMachine;
        const gs = app.gameState;
        const timeScale = gs.getEffectiveTimeScale();

        const currentSpeed = dsm.currentSpeed;
        const maxSpeed = dsm.getCurrentMaxSpeed();
        const baseSpeed = dsm.getCurrentBaseSpeed();
        const acceleration = dsm.getCurrentAcceleration();
        const threshold80 = maxSpeed * 0.80;
        const aero = gs.getCurrentAerodynamics();

        // Calculate drag equilibrium
        const dragEquilibrium = baseSpeed + (acceleration / aero);

        const grid = document.createElement('div');
        grid.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 5px;';

        const rows = [
            ['Current Speed:', `${currentSpeed.toFixed(1)} mph`, '#fff'],
            ['Base Speed:', `${baseSpeed.toFixed(1)} mph`, '#fff'],
            ['Max Speed:', `${maxSpeed.toFixed(1)} mph`, '#fff'],
            ['80% Threshold:', `${threshold80.toFixed(1)} mph`, currentSpeed >= threshold80 ? '#27ae60' : '#e74c3c'],
            ['Acceleration:', `${acceleration.toFixed(2)} mph/click`, '#fff'],
            ['Aerodynamics:', aero.toFixed(3), '#fff'],
            ['Drag Equilibrium:', `${dragEquilibrium.toFixed(1)} mph`, dragEquilibrium >= threshold80 ? '#27ae60' : '#e74c3c'],
            ['TimeScale:', `${timeScale.toFixed(2)}x`, '#f39c12'],
            ['Autopilot:', dsm.cruiseControlActive ? 'ACTIVE' : 'inactive', dsm.cruiseControlActive ? '#27ae60' : '#666']
        ];

        rows.forEach(([label, value, color]) => {
            createDiagRow(label, value, color).forEach(el => grid.appendChild(el));
        });

        this.speedDiagnostics.appendChild(grid);

        if (dragEquilibrium < threshold80) {
            this.speedDiagnostics.appendChild(createWarning('Warning: Drag equilibrium is below 80% threshold!'));
        }
    }

    refreshWaveInfo() {
        const waves = this.gameState.state.waves;
        const totalMiles = this.gameState.state.totalMiles;
        const nextMilestone = waves.nextMilestone;
        const availablePacks = waves.availablePacks || [];

        // Calculate expected next milestone
        const interval = 100; // from items.json milestoneGeneration
        const expectedNext = Math.ceil(totalMiles / interval) * interval;
        const isStuck = nextMilestone !== null && nextMilestone <= totalMiles - interval;

        clearChildren(this.waveInfo);

        const grid = document.createElement('div');
        grid.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 5px;';

        const rows = [
            ['Total Miles:', totalMiles.toFixed(1), '#fff'],
            ['Next Milestone:', `${nextMilestone}${isStuck ? ' (STUCK!)' : ''}`, isStuck ? '#e74c3c' : '#fff'],
            ['Expected Next:', String(expectedNext), '#27ae60'],
            ['Available Packs:', String(availablePacks.length), '#fff'],
            ['Packs Purchased:', String(waves.packsPurchased || 0), '#fff']
        ];

        rows.forEach(([label, value, color]) => {
            createDiagRow(label, value, color).forEach(el => grid.appendChild(el));
        });

        this.waveInfo.appendChild(grid);

        if (isStuck) {
            this.waveInfo.appendChild(createWarning('Warning: nextMilestone appears stuck! Click "Fix Next Milestone" to repair.'));
        }

        if (availablePacks.length > 0) {
            const milestones = availablePacks.filter(p => p != null).map(p => p.milestone || p.totalMiles).join(', ');
            this.waveInfo.appendChild(createInfo(`Available pack milestones: ${milestones}`));
        }
    }

    fixNextMilestone() {
        const totalMiles = this.gameState.state.totalMiles;
        const interval = 100; // from items.json milestoneGeneration

        // Calculate what the next milestone should be
        const newNextMilestone = Math.ceil(totalMiles / interval) * interval;

        // Make sure it's actually ahead of current miles
        const finalMilestone = newNextMilestone <= totalMiles ? newNextMilestone + interval : newNextMilestone;

        const oldMilestone = this.gameState.state.waves.nextMilestone;
        this.gameState.state.waves.nextMilestone = finalMilestone;
        this.gameState.save(true);

        this.showNotification(`Fixed nextMilestone: ${oldMilestone} -> ${finalMilestone}`);
        this.refreshWaveInfo();
    }

    generateRandomWave() {
        // Generate a random wave pack with random quality
        const quality = Math.random(); // 0.0 to 1.0
        const totalMiles = this.gameState.state.totalMiles;

        // Create a unique milestone based on current time
        const milestone = totalMiles + Date.now() % 10000;

        // Determine tier from quality and use flat tier-based cost
        const tier = this._getQualityTier(quality);
        const cost = LOT_OPEN_COST[tier] || LOT_OPEN_COST.faded;

        // Create pack data
        const packData = {
            milestone: milestone,
            totalMiles: milestone,
            cost: cost,
            packQuality: quality,
            isTutorial: false
        };

        // Add to available packs (uses safeguarded method)
        this.gameState.addAvailablePack(packData);

        // Get tier name for notification
        const tierNames = ['Faded', 'Standard', 'Pristine', 'Golden', 'Vectorized'];
        const tierIndex = Math.min(Math.floor(quality * 5), 4);
        const tierName = tierNames[tierIndex];

        this.showNotification(`Generated ${tierName} wave (quality: ${(quality * 100).toFixed(1)}%)`);
        this.refreshWaveInfo();

        // Update UI badge if available
        const app = window.roadTripApp;
        if (app && app.gameUI) {
            app.gameUI.updatePackNotificationBadge();
        }
    }

    /**
     * Get tier name from quality value
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} - Tier name
     */
    _getQualityTier(quality) {
        if (quality >= QUALITY_TIER_THRESHOLDS.vectorized) return 'vectorized';
        if (quality >= QUALITY_TIER_THRESHOLDS.golden) return 'golden';
        if (quality >= QUALITY_TIER_THRESHOLDS.pristine) return 'pristine';
        if (quality >= QUALITY_TIER_THRESHOLDS.standard) return 'standard';
        return 'faded';
    }

    spawnTestPickup() {
        if (!this.wavePickupManager) {
            this.showNotification('Wave pickup manager not available');
            return;
        }

        const pickup = this.wavePickupManager.spawnTestPickup(100);
        if (pickup) {
            this.showNotification('Spawned test pickup 100 units ahead');
        } else {
            this.showNotification('Failed to spawn test pickup');
        }
    }

    generateItem() {
        const baseItemId = this.baseItemSelect.value;
        const rarity = this.raritySelect.value;

        if (baseItemId) {
            this.currentItem = this.itemGenerator.generateItem(baseItemId, rarity);
        } else {
            this.currentItem = this.itemGenerator.generateRandomItem(rarity);
        }

        if (this.currentItem) {
            this.renderItemPreview();
            this.addBtn.style.display = 'block';
        }
    }

    renderItemPreview() {
        const item = this.currentItem;
        const rarityColor = this.itemsData.rarityColors[item.rarity];

        clearChildren(this.previewArea);

        // Name row
        const nameRow = document.createElement('div');
        nameRow.style.marginBottom = '10px';

        const nameStrong = document.createElement('strong');
        nameStrong.style.cssText = `color: ${rarityColor}; font-size: 16px;`;
        nameStrong.textContent = item.name;
        nameRow.appendChild(nameStrong);

        const slotSpan = document.createElement('span');
        slotSpan.style.cssText = 'color: #666; font-size: 12px; margin-left: 10px;';
        slotSpan.textContent = item.slotType || item.slot || 'item';
        nameRow.appendChild(slotSpan);

        this.previewArea.appendChild(nameRow);

        // Rarity row
        const rarityDiv = document.createElement('div');
        rarityDiv.style.cssText = `color: ${rarityColor}; text-transform: uppercase; font-size: 11px; margin-bottom: 10px;`;
        rarityDiv.textContent = item.rarity;
        this.previewArea.appendChild(rarityDiv);

        // Effects list
        const effectsDiv = document.createElement('div');
        effectsDiv.style.cssText = 'font-size: 13px; color: #aaa;';

        item.effects.forEach(effect => {
            // Determine if this effect should be displayed as a multiplier (percentage)
            const isMultiplier = effect.operation === 'multiply' ||
                (effect.operation !== 'add' && (effect.type === 'acceleration' || effect.type === 'timeScale'));

            let valueDisplay;
            if (isMultiplier) {
                const percent = Math.round((effect.value - 1) * 100);
                valueDisplay = `${percent >= 0 ? '+' : ''}${percent}%`;
            } else {
                const formattedValue = Number(effect.value.toFixed(2));
                valueDisplay = `${formattedValue >= 0 ? '+' : ''}${formattedValue}`;
            }

            const effectLine = document.createElement('div');
            effectLine.style.margin = '3px 0';
            effectLine.appendChild(document.createTextNode(`${effect.type}: ${valueDisplay}`));

            if (effect.condition) {
                const condSpan = document.createElement('span');
                condSpan.style.color = '#f39c12';
                condSpan.textContent = ` (${effect.condition.timeOfDay})`;
                effectLine.appendChild(condSpan);
            }

            effectsDiv.appendChild(effectLine);
        });

        this.previewArea.appendChild(effectsDiv);
    }

    addItemToInventory() {
        if (!this.currentItem) return;

        const success = this.gameState.addItem(this.currentItem);
        if (success) {
            this.showNotification(`Added "${this.currentItem.name}" to inventory!`);
            this.currentItem = null;
            clearChildren(this.previewArea);
            this.previewArea.appendChild(createPlaceholder('Item added! Generate another?', '#27ae60'));
            this.addBtn.style.display = 'none';
        } else {
            this.showNotification('Failed to add item (duplicate ID?)', true);
        }
    }

    showNotification(message, isError = false) {
        const notif = document.createElement('div');
        notif.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: ${isError ? '#e74c3c' : '#27ae60'};
            color: white;
            padding: 10px 20px;
            border-radius: 5px;
            z-index: 10001;
            font-family: 'Courier New', monospace;
            animation: fadeInOut 2s ease-in-out forwards;
        `;
        notif.textContent = message;

        // Add animation keyframes if not already added
        if (!document.getElementById('debug-notif-styles')) {
            const style = document.createElement('style');
            style.id = 'debug-notif-styles';
            style.textContent = `
                @keyframes fadeInOut {
                    0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
                    15% { opacity: 1; transform: translateX(-50%) translateY(0); }
                    85% { opacity: 1; transform: translateX(-50%) translateY(0); }
                    100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
                }
            `;
            document.head.appendChild(style);
        }

        document.body.appendChild(notif);
        setTimeout(() => notif.remove(), 2000);
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.overlay.style.display = 'flex';
        this.isOpen = true;

        // Update time slider and display to current game time
        if (this.timeSlider) {
            this.timeSlider.value = this.gameState.state.gameTime;
        }
        this.updateTimeDisplay();
    }

    close() {
        this.overlay.style.display = 'none';
        this.isOpen = false;
    }

    /**
     * Toggle performance stats overlay visibility
     * @param {boolean} enabled - Whether to show the performance stats
     */
    togglePerfStats(enabled) {
        if (enabled) {
            // Create perf stats overlay if it doesn't exist
            if (!this.perfStatsOverlay) {
                this.perfStatsOverlay = document.createElement('div');
                this.perfStatsOverlay.id = 'perf-stats-overlay';
                this.perfStatsOverlay.style.cssText = `
                    position: fixed;
                    top: 50px;
                    left: 10px;
                    background: rgba(0, 0, 0, 0.85);
                    color: #0f0;
                    font-family: 'IBM Plex Mono', 'Courier New', monospace;
                    font-size: 11px;
                    padding: 10px 12px;
                    border: 1px solid rgba(0, 255, 0, 0.3);
                    z-index: 9999;
                    min-width: 180px;
                    pointer-events: none;
                `;
                document.body.appendChild(this.perfStatsOverlay);

                // Start update loop
                this.startPerfStatsUpdates();
            }
            this.perfStatsOverlay.style.display = 'block';
        } else {
            if (this.perfStatsOverlay) {
                this.perfStatsOverlay.style.display = 'none';
            }
        }
    }

    /**
     * Start the performance stats update loop
     */
    startPerfStatsUpdates() {
        // FPS tracking with rolling average
        let lastTime = performance.now();
        let frameCount = 0;
        let fps = 0;

        // Frame time tracking
        let frameTimes = [];
        const maxFrameSamples = 60;
        let lastFrameTime = performance.now();

        // Create DOM structure once, then update values in loop (more efficient)
        let statsElements = null;
        let waitingMessage = null;

        const createStatsStructure = () => {
            clearChildren(this.perfStatsOverlay);

            const header = document.createElement('div');
            header.style.cssText = 'color: #888; margin-bottom: 6px; font-size: 10px; letter-spacing: 1px;';
            header.textContent = 'PERFORMANCE';
            this.perfStatsOverlay.appendChild(header);

            const fpsRow = createPerfRow('FPS', '0', '#0f0');
            fpsRow._valueSpan.style.fontWeight = 'bold';
            this.perfStatsOverlay.appendChild(fpsRow);

            const frameRow = createPerfRow('Frame', '0ms', '#0f0');
            this.perfStatsOverlay.appendChild(frameRow);

            const minMaxRow = createPerfRow('Min/Max', '0/0ms', '#666');
            this.perfStatsOverlay.appendChild(minMaxRow);

            this.perfStatsOverlay.appendChild(createPerfDivider());

            const drawCallsRow = createPerfRow('Draw Calls', '0', '#4af');
            this.perfStatsOverlay.appendChild(drawCallsRow);

            const trianglesRow = createPerfRow('Triangles', '0', '#4af');
            this.perfStatsOverlay.appendChild(trianglesRow);

            this.perfStatsOverlay.appendChild(createPerfDivider());

            const geometriesRow = createPerfRow('Geometries', '0', '#a8f');
            this.perfStatsOverlay.appendChild(geometriesRow);

            const texturesRow = createPerfRow('Textures', '0', '#a8f');
            this.perfStatsOverlay.appendChild(texturesRow);

            return {
                fps: fpsRow._valueSpan,
                frame: frameRow._valueSpan,
                minMax: minMaxRow._valueSpan,
                drawCalls: drawCallsRow._valueSpan,
                triangles: trianglesRow._valueSpan,
                geometries: geometriesRow._valueSpan,
                textures: texturesRow._valueSpan
            };
        };

        const formatNum = (n) => {
            if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
            if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
            return n.toString();
        };

        const update = () => {
            // Always schedule next frame to keep loop alive
            requestAnimationFrame(update);

            // Skip processing if overlay is hidden
            if (!this.perfStatsOverlay || this.perfStatsOverlay.style.display === 'none') {
                return;
            }

            const now = performance.now();

            // Track frame time
            const frameTime = now - lastFrameTime;
            lastFrameTime = now;
            frameTimes.push(frameTime);
            if (frameTimes.length > maxFrameSamples) {
                frameTimes.shift();
            }

            // Calculate FPS every second
            frameCount++;
            if (now - lastTime >= 1000) {
                fps = Math.round(frameCount * 1000 / (now - lastTime));
                frameCount = 0;
                lastTime = now;
            }

            const app = window.roadTripApp;
            if (!app || !app.renderer) {
                if (!waitingMessage) {
                    clearChildren(this.perfStatsOverlay);
                    waitingMessage = document.createElement('div');
                    waitingMessage.style.color = '#666';
                    waitingMessage.textContent = 'Waiting for renderer...';
                    this.perfStatsOverlay.appendChild(waitingMessage);
                    statsElements = null;
                }
                return;
            }

            // Create structure on first valid render
            if (!statsElements) {
                waitingMessage = null;
                statsElements = createStatsStructure();
            }

            // Get Three.js renderer info
            const info = app.renderer.info;
            const render = info.render;
            const memory = info.memory;

            // Calculate frame time stats
            const avgFrameTime = frameTimes.reduce((a, b) => a + b, 0) / frameTimes.length;
            const maxFrameTime = Math.max(...frameTimes);
            const minFrameTime = Math.min(...frameTimes);

            // Update values (much faster than rebuilding innerHTML)
            const fpsColor = fps >= 55 ? '#0f0' : fps >= 30 ? '#ff0' : '#f00';
            const ftColor = avgFrameTime < 16.67 ? '#0f0' : avgFrameTime < 33.33 ? '#ff0' : '#f00';

            statsElements.fps.textContent = fps;
            statsElements.fps.style.color = fpsColor;
            statsElements.frame.textContent = `${avgFrameTime.toFixed(1)}ms`;
            statsElements.frame.style.color = ftColor;
            statsElements.minMax.textContent = `${minFrameTime.toFixed(1)}/${maxFrameTime.toFixed(1)}ms`;
            statsElements.drawCalls.textContent = render.calls;
            statsElements.triangles.textContent = formatNum(render.triangles);
            statsElements.geometries.textContent = memory.geometries;
            statsElements.textures.textContent = memory.textures;
        };

        requestAnimationFrame(update);
    }

    /**
     * Toggle free camera mode for debugging
     * @param {boolean} enabled - Whether to enable free camera
     */
    toggleFreeCamera(enabled) {
        const app = window.roadTripApp;
        if (!app || !app.camera || !app.renderer) {
            this.showNotification('Game not loaded yet', true);
            return;
        }

        if (enabled) {
            // Create a separate debug camera, copying settings from main camera
            if (!this._debugCamera) {
                this._debugCamera = new THREE.PerspectiveCamera(
                    app.camera.fov,
                    app.camera.aspect,
                    app.camera.near,
                    app.camera.far
                );
            }

            // Copy current main camera position as starting point
            this._debugCamera.position.copy(app.camera.position);
            this._debugCamera.rotation.copy(app.camera.rotation);

            // Store original camera reference
            this._originalCamera = app.camera;

            // Store original render function if not already stored
            if (!this._originalRender) {
                this._originalRender = app.renderer.render.bind(app.renderer);
            }

            // Override render to use debug camera
            const debugCam = this._debugCamera;
            const scene = app.scene;
            app.renderer.render = (scn, cam) => {
                this._originalRender(scn, debugCam);
            };

            app.freeCameraEnabled = true;

            // Initialize free camera state
            this._freeCamState = {
                yaw: -app.camera.rotation.y, // Start facing same direction
                pitch: app.camera.rotation.x,
                moveSpeed: 0.5,              // Base speed per frame (reduced 80% from original)
                lookSpeed: 0.002,
                keys: { w: false, a: false, s: false, d: false, q: false, e: false, shift: false },
                isPointerLocked: false,
                lastTime: performance.now()
            };

            // Create HUD overlay
            this._createFreeCamHUD();

            // Set up keyboard controls
            this._freeCamKeyDown = (e) => {
                if (this.isOpen) return; // Don't capture while menu is open
                const key = e.key.toLowerCase();
                if (key in this._freeCamState.keys) {
                    this._freeCamState.keys[key] = true;
                    e.preventDefault();
                }
                if (e.shiftKey) this._freeCamState.keys.shift = true;
            };
            this._freeCamKeyUp = (e) => {
                const key = e.key.toLowerCase();
                if (key in this._freeCamState.keys) {
                    this._freeCamState.keys[key] = false;
                }
                if (!e.shiftKey) this._freeCamState.keys.shift = false;
            };

            // Set up mouse look (pointer lock)
            this._freeCamMouseMove = (e) => {
                if (!this._freeCamState.isPointerLocked) return;

                this._freeCamState.yaw -= e.movementX * this._freeCamState.lookSpeed;
                this._freeCamState.pitch -= e.movementY * this._freeCamState.lookSpeed;

                // Clamp pitch to avoid flipping
                this._freeCamState.pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, this._freeCamState.pitch));
            };

            this._freeCamPointerLockChange = () => {
                this._freeCamState.isPointerLocked = document.pointerLockElement === app.renderer.domElement;
                this._updateFreeCamHUD();
            };

            this._freeCamClick = () => {
                if (!this._freeCamState.isPointerLocked && app.freeCameraEnabled && !this.isOpen) {
                    app.renderer.domElement.requestPointerLock();
                }
            };

            document.addEventListener('keydown', this._freeCamKeyDown);
            document.addEventListener('keyup', this._freeCamKeyUp);
            document.addEventListener('mousemove', this._freeCamMouseMove);
            document.addEventListener('pointerlockchange', this._freeCamPointerLockChange);
            app.renderer.domElement.addEventListener('click', this._freeCamClick);

            // Start free camera update loop
            this._freeCamUpdate = () => {
                if (!app.freeCameraEnabled) return;

                const camera = this._debugCamera;
                const state = this._freeCamState;

                // Calculate delta time for frame-rate independent movement
                const now = performance.now();
                const deltaTime = (now - state.lastTime) / 16.67; // Normalize to ~60fps
                state.lastTime = now;

                const speed = (state.keys.shift ? state.moveSpeed * 3 : state.moveSpeed) * deltaTime;

                // Get forward and right vectors from current camera orientation
                const forward = new THREE.Vector3(0, 0, -1);
                const right = new THREE.Vector3(1, 0, 0);
                const up = new THREE.Vector3(0, 1, 0);

                // Apply yaw and pitch to get look direction
                forward.applyAxisAngle(up, state.yaw);
                forward.applyAxisAngle(right.clone().applyAxisAngle(up, state.yaw), state.pitch);

                right.applyAxisAngle(up, state.yaw);

                // Movement
                if (state.keys.w) camera.position.addScaledVector(forward, speed);
                if (state.keys.s) camera.position.addScaledVector(forward, -speed);
                if (state.keys.a) camera.position.addScaledVector(right, -speed);
                if (state.keys.d) camera.position.addScaledVector(right, speed);
                if (state.keys.q) camera.position.y -= speed;
                if (state.keys.e) camera.position.y += speed;

                // Update camera look direction
                const lookTarget = camera.position.clone().add(forward);
                camera.lookAt(lookTarget);

                // Update HUD with position info
                this._updateFreeCamHUD();

                requestAnimationFrame(this._freeCamUpdate);
            };
            this._freeCamUpdate();

            this.showNotification('Free camera enabled - click game to look around');
        } else {
            // Disable free camera
            app.freeCameraEnabled = false;

            // Restore original render function
            if (this._originalRender) {
                app.renderer.render = this._originalRender;
            }

            // Exit pointer lock
            if (document.pointerLockElement) {
                document.exitPointerLock();
            }

            // Remove event listeners
            if (this._freeCamKeyDown) document.removeEventListener('keydown', this._freeCamKeyDown);
            if (this._freeCamKeyUp) document.removeEventListener('keyup', this._freeCamKeyUp);
            if (this._freeCamMouseMove) document.removeEventListener('mousemove', this._freeCamMouseMove);
            if (this._freeCamPointerLockChange) document.removeEventListener('pointerlockchange', this._freeCamPointerLockChange);
            if (this._freeCamClick) app.renderer.domElement.removeEventListener('click', this._freeCamClick);

            // Remove HUD
            if (this._freeCamHUD) {
                this._freeCamHUD.remove();
                this._freeCamHUD = null;
            }

            this.showNotification('Free camera disabled');
        }
    }

    _createFreeCamHUD() {
        this._freeCamHUD = document.createElement('div');
        this._freeCamHUD.id = 'free-cam-hud';
        this._freeCamHUD.style.cssText = `
            position: fixed;
            top: 10px;
            left: 10px;
            background: rgba(0, 0, 0, 0.8);
            color: #0ff;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            padding: 10px;
            border: 1px solid #0ff;
            border-radius: 5px;
            z-index: 9999;
            min-width: 200px;
        `;

        // Create structure once for efficient updates
        const title = document.createElement('div');
        title.style.cssText = 'color: #f39c12; margin-bottom: 5px; font-weight: bold;';
        title.textContent = 'FREE CAMERA';
        this._freeCamHUD.appendChild(title);

        const posLabel = document.createElement('div');
        posLabel.textContent = 'Position:';
        this._freeCamHUD.appendChild(posLabel);

        this._freeCamPosX = document.createElement('div');
        this._freeCamHUD.appendChild(this._freeCamPosX);

        this._freeCamPosY = document.createElement('div');
        this._freeCamHUD.appendChild(this._freeCamPosY);

        this._freeCamPosZ = document.createElement('div');
        this._freeCamHUD.appendChild(this._freeCamPosZ);

        this._freeCamYaw = document.createElement('div');
        this._freeCamYaw.style.marginTop = '5px';
        this._freeCamHUD.appendChild(this._freeCamYaw);

        this._freeCamPitch = document.createElement('div');
        this._freeCamHUD.appendChild(this._freeCamPitch);

        this._freeCamStatus = document.createElement('div');
        this._freeCamStatus.style.cssText = 'margin-top: 10px; border-top: 1px solid #0ff; padding-top: 5px; font-size: 10px; color: #888;';
        this._freeCamHUD.appendChild(this._freeCamStatus);

        const instructions = document.createElement('div');
        instructions.style.cssText = 'font-size: 10px; color: #888; margin-top: 5px;';
        instructions.textContent = 'WASD: Move | Q/E: Down/Up';
        this._freeCamHUD.appendChild(instructions);

        const instructions2 = document.createElement('div');
        instructions2.style.cssText = 'font-size: 10px; color: #888;';
        instructions2.textContent = 'Shift: Fast | ESC: Release mouse';
        this._freeCamHUD.appendChild(instructions2);

        document.body.appendChild(this._freeCamHUD);
        this._updateFreeCamHUD();
    }

    _updateFreeCamHUD() {
        if (!this._freeCamHUD || !this._debugCamera) return;
        const app = window.roadTripApp;
        if (!app) return;

        const cam = this._debugCamera;
        const state = this._freeCamState;

        // Update values only (structure is already created)
        this._freeCamPosX.textContent = `  X: ${cam.position.x.toFixed(1)}`;
        this._freeCamPosY.textContent = `  Y: ${cam.position.y.toFixed(1)}`;
        this._freeCamPosZ.textContent = `  Z: ${cam.position.z.toFixed(1)}`;
        this._freeCamYaw.textContent = `Yaw: ${(state.yaw * 180 / Math.PI).toFixed(1)}°`;
        this._freeCamPitch.textContent = `Pitch: ${(state.pitch * 180 / Math.PI).toFixed(1)}°`;

        // Update status with dynamic content
        clearChildren(this._freeCamStatus);
        if (state.isPointerLocked) {
            const span = document.createElement('span');
            span.style.color = '#0f0';
            span.textContent = 'Mouse captured';
            this._freeCamStatus.appendChild(span);
            this._freeCamStatus.appendChild(document.createTextNode(' (ESC to release)'));
        } else {
            const span = document.createElement('span');
            span.style.color = '#f00';
            span.textContent = 'Click game to capture mouse';
            this._freeCamStatus.appendChild(span);
        }
    }

    /**
     * Export a bug report from Firestore to a JSON file
     * @param {string} docId - The document ID of the bug report
     */
    async exportBugReport(docId) {
        if (!docId) {
            this.showNotification('Please enter a document ID', true);
            return;
        }

        try {
            // Import Firestore functions dynamically (same as BugReportManager uses)
            const { doc, getDoc } = await import('https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js');
            const { db, isConfigured } = await import('../firebase/config.js');

            if (!isConfigured || !db) {
                this.showNotification('Firebase not configured', true);
                return;
            }

            this.showNotification('Fetching bug report...');

            const docRef = doc(db, 'bugReports', docId);
            const snapshot = await getDoc(docRef);

            if (!snapshot.exists()) {
                this.showNotification('Bug report not found', true);
                return;
            }

            const data = { id: snapshot.id, ...snapshot.data() };

            // Convert Firestore timestamps to ISO strings
            const convertTimestamps = (obj) => {
                if (obj === null || obj === undefined) return obj;
                if (typeof obj !== 'object') return obj;

                // Check for Firestore Timestamp
                if (obj.toDate && typeof obj.toDate === 'function') {
                    return obj.toDate().toISOString();
                }

                // Check for timestamp-like object with seconds
                if (obj.seconds !== undefined && obj.nanoseconds !== undefined) {
                    return new Date(obj.seconds * 1000).toISOString();
                }

                if (Array.isArray(obj)) {
                    return obj.map(convertTimestamps);
                }

                const result = {};
                for (const [key, value] of Object.entries(obj)) {
                    result[key] = convertTimestamps(value);
                }
                return result;
            };

            const cleanData = convertTimestamps(data);
            const jsonStr = JSON.stringify(cleanData, null, 2);

            // Create and trigger download
            const blob = new Blob([jsonStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `bugreport_${docId}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            this.showNotification(`Exported bug report: ${docId}`);
        } catch (error) {
            console.error('[DebugMenu] Failed to export bug report:', error);
            this.showNotification(`Export failed: ${error.message}`, true);
        }
    }

    /**
     * Show a modal for debugging purposes
     * @param {string} modalId - The modal to show
     */
    showDebugModal(modalId) {
        if (!this.gameUI) {
            this.showNotification('GameUI not available');
            return;
        }

        // Close debug menu first so modal is visible
        this.close();

        switch (modalId) {
            case 'waveTutorial':
                this.gameUI.showWaveTutorialModal();
                break;
            case 'tierUpgrade':
                // Show tier upgrade modal via upgrade menu
                if (this.gameUI.upgradeMenu) {
                    this.gameUI.upgradeMenu._showTierUpgradeModal({
                        oldTier: 'standard',
                        newTier: 'pristine',
                        oldMultiplier: 1.0,
                        newMultiplier: 1.15
                    });
                } else {
                    this.showNotification('Upgrade menu not available');
                }
                break;
            case 'saveConflict':
                // Show save conflict modal with mock data
                this.gameUI.showSaveConflictModal({
                    localState: {
                        lifetimeMiles: 1250,
                        _lastSaveTime: Date.now() - 300000 // 5 mins ago
                    },
                    cloudState: {
                        lifetimeMiles: 8420
                    },
                    cloudTimestamp: Date.now() - 86400000, // 1 day ago
                    onChooseLocal: () => this.showNotification('Chose: Local'),
                    onChooseCloud: () => this.showNotification('Chose: Cloud')
                });
                break;
            default:
                this.showNotification(`Unknown modal: ${modalId}`);
        }
    }
}
